import React, { useCallback } from 'react';
import type {
  ProductCarouselNavigatePayload,
  ProductCarouselProductPayload,
} from '@noths/polaris-design-system-candidate-components';
import { ProductCarousel } from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { usePlpPreview } from './hooks/usePlpPreview';
import {
  trackPlpPreviewImpression,
  trackPlpPreviewNavigation,
  trackPlpPreviewProductClick,
  trackPlpPreviewSeeAllClick,
} from './tracking/plpPreviewCarouselEvents';

export const getSeeAllLinkText = (heading: string) => `See all ${heading}`;

export interface PlpPreviewProductsProps {
  currency: CurrencyCode;
  heading: string;
  /** The page's own pageType, the `<location>` half of the tracked list value. */
  pageType: string;
  plpUrl: string;
  trackingPosition: number;
  /**
   * Unread here, but load-bearing: transformModularContentToUISections uses the
   * presence of this prop to decide whether a section is assigned a
   * trackingPosition at all, and increments a page-wide counter when it is.
   * Removing it would shift content_position for every section below this one.
   */
  trackingTitle: string;
}

export const PlpPreviewProducts = ({
  currency,
  heading,
  pageType,
  plpUrl,
  trackingPosition,
}: PlpPreviewProductsProps) => {
  const { products, productsToTrack } = usePlpPreview({ currency, plpUrl });

  const numberOfItems = productsToTrack.length;

  /* `direction` is passed straight through: the carousel's own
     ProductCarouselDirection is already the `'next' | 'prev'` the tracking module
     takes. Only `method` needs narrowing, to the `swiped` boolean GA reports. */
  const onNavigate = useCallback(
    ({ direction, method }: ProductCarouselNavigatePayload) => {
      trackPlpPreviewNavigation({ direction, nodeName: heading, swiped: method === 'swipe' });
    },
    [heading],
  );

  /* The payload's own `product` is the OOUX shape the card renders, which does
     not carry what tracking needs (the product code, the partner shortcode,
     `freeDomesticDelivery`, `salePercentage`), so the index is used to look the
     service product back up. */
  const onProductClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, { index }: ProductCarouselProductPayload) => {
      const product = productsToTrack[index];

      if (!product) {
        return;
      }

      trackPlpPreviewProductClick(e, {
        contentPosition: trackingPosition,
        nodeName: heading,
        numberOfItems,
        pageType,
        product,
        productIndex: index,
      });
    },
    [heading, numberOfItems, pageType, productsToTrack, trackingPosition],
  );

  /* One impression event for the whole carousel, listing every product, rather
     than the per-product `onProductVisible`.

     No empty-shelf guard here: trackPlpPreviewImpression already no-ops on an
     empty list, and the PDP copy of this carousel leaves the decision to the
     tracking module in the same way. */
  const onVisible = useCallback(() => {
    trackPlpPreviewImpression({
      contentPosition: trackingPosition,
      nodeName: heading,
      pageType,
      products: productsToTrack,
    });
  }, [heading, pageType, productsToTrack, trackingPosition]);

  const onSeeAllClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      trackPlpPreviewSeeAllClick(e, { href: plpUrl, nodeName: heading });
    },
    [heading, plpUrl],
  );

  return (
    <ProductCarousel
      heading={heading}
      onNavigate={onNavigate}
      onProductClick={onProductClick}
      onVisible={onVisible}
      products={products}
      subheadingLink={{ href: plpUrl, onClick: onSeeAllClick, text: getSeeAllLinkText(heading) }}
    />
  );
};
